import { createElement, Fragment, type ReactNode } from "react";
import { FaHome } from "react-icons/fa";

import urls from "../../urls.jsx";
import TimeDisplay from "../TimeDisplay/index.jsx";

type NamedEntity = {
    name?: string | null;
};

type TimestampedEntity = NamedEntity & {
    timestamp?: {
        seconds?: number | null;
    } | null;
};

export type BreadcrumbData = {
    app?: NamedEntity | null;
    dock?: NamedEntity | null;
    organization?: NamedEntity | null;
    location?: NamedEntity | null;
    mission?: TimestampedEntity | null;
    payload?: NamedEntity | null;
    routine?: NamedEntity | null;
    task?: NamedEntity | null;
    flight?: TimestampedEntity | null;
};

export type BreadcrumbDataKey = keyof BreadcrumbData;

export type BreadcrumbContext = BreadcrumbData & {
    isAdmin: boolean;
    params: Record<string, string | undefined>;
    query: URLSearchParams;
};

type BreadcrumbItemBase = {
    label: (context: BreadcrumbContext) => ReactNode;
    to?: (context: BreadcrumbContext) => string;
    visible?: (context: BreadcrumbContext) => boolean;
};

type StaticBreadcrumbItem = BreadcrumbItemBase & {
    dataKey?: never;
};

type DynamicBreadcrumbItem<DataKey extends BreadcrumbDataKey> =
    BreadcrumbItemBase & {
        dataKey: DataKey;
    };

type BreadcrumbItem =
    | StaticBreadcrumbItem
    | DynamicBreadcrumbItem<BreadcrumbDataKey>;

export type BreadcrumbPath<DataKey extends BreadcrumbDataKey = never> = {
    items: readonly BreadcrumbItem[];
    dataKeys: readonly DataKey[];
};

export type BreadcrumbPathData<Path extends BreadcrumbPath<BreadcrumbDataKey>> =
    Path extends BreadcrumbPath<infer DataKey>
        ? Pick<BreadcrumbData, DataKey>
        : never;

export function defineItem(item: StaticBreadcrumbItem): StaticBreadcrumbItem;
export function defineItem<DataKey extends BreadcrumbDataKey>(
    item: DynamicBreadcrumbItem<DataKey>,
): DynamicBreadcrumbItem<DataKey>;
export function defineItem(item: BreadcrumbItem): BreadcrumbItem {
    return item;
}

type DataKeyFromItem<Item> =
    Item extends DynamicBreadcrumbItem<infer DataKey> ? DataKey : never;

export function definePath<Items extends readonly BreadcrumbItem[]>(
    items: Items,
): BreadcrumbPath<DataKeyFromItem<Items[number]>> {
    const dataKeys = new Set<BreadcrumbDataKey>();

    for (const item of items) {
        if (item.dataKey) {
            dataKeys.add(item.dataKey);
        }
    }

    return {
        items,
        dataKeys: [...dataKeys] as DataKeyFromItem<Items[number]>[],
    };
}

const routeId = (context: BreadcrumbContext, id: string) => context.params[id];

const locationId = (context: BreadcrumbContext) =>
    routeId(context, "locationId");
const missionId = (context: BreadcrumbContext) =>
    routeId(context, "missionId") ??
    context.query.get("missionId") ??
    undefined;
const flightId = (context: BreadcrumbContext) =>
    routeId(context, "flightId") ?? context.query.get("flightId") ?? undefined;

const timestampLabel = (
    type: "Mission" | "Flight",
    timestamp: TimestampedEntity["timestamp"],
    location: BreadcrumbData["location"],
) => {
    const seconds = timestamp?.seconds;

    if (typeof seconds !== "number") {
        return type;
    }

    return createElement(
        Fragment,
        null,
        `${type} `,
        createElement(TimeDisplay, {
            timestamp: seconds,
            format: "YYYY-MM-DD HH:mm:ss",
            hideDateIfToday: undefined,
            breakOnHashtag: undefined,
            organizationId: undefined,
            locationId: undefined,
            location,
        }),
    );
};

export const organizationsItem = defineItem({
    label: () => createElement(FaHome),
    to: () => urls.organizations(),
    visible: ({ isAdmin }) => isAdmin,
});

export const organizationItem = defineItem({
    dataKey: "organization",
    label: ({ isAdmin, organization }) =>
        isAdmin
            ? (organization?.name ?? "Organization")
            : createElement(FaHome),
    to: (context) => urls.organization(routeId(context, "organizationId")),
});

export const locationsItem = defineItem({
    label: () => "Locations",
    to: (context) => urls.locations(routeId(context, "organizationId")),
});

export const locationItem = defineItem({
    dataKey: "location",
    label: ({ location }) => location?.name ?? "Location",
    to: (context) =>
        urls.location(routeId(context, "organizationId"), locationId(context)),
    visible: (context) => {
        const id = locationId(context);
        return Boolean(id && id !== "undefined");
    },
});

export const tasksItem = defineItem({
    label: () => "Tasks",
    to: (context) =>
        urls.tasks(routeId(context, "organizationId"), locationId(context)),
});

export const taskItem = defineItem({
    dataKey: "task",
    label: ({ task }) => task?.name ?? "Task",
    to: (context) =>
        urls.task(
            routeId(context, "organizationId"),
            locationId(context),
            routeId(context, "taskId"),
        ),
});

export const docksItem = defineItem({
    label: () => "Docks",
    to: (context) =>
        urls.docks(routeId(context, "organizationId"), locationId(context)),
});

export const dockItem = defineItem({
    dataKey: "dock",
    label: ({ dock }) => dock?.name ?? "Dock",
    to: (context) =>
        urls.dock(
            routeId(context, "organizationId"),
            locationId(context),
            routeId(context, "dockId"),
        ),
});

export const dronesItem = defineItem({
    label: () => "Drones",
    to: (context) =>
        urls.locationDrones(
            routeId(context, "organizationId"),
            locationId(context),
        ),
});

export const droneItem = defineItem({
    label: (context) => routeId(context, "droneId"),
    to: (context) =>
        urls.locationDrone(
            routeId(context, "organizationId"),
            locationId(context),
            routeId(context, "droneId"),
        ),
});

export const missionsItem = defineItem({
    label: () => "Missions",
    to: (context) =>
        urls.locationMissions(
            routeId(context, "organizationId"),
            locationId(context),
        ),
});

export const missionItem = defineItem({
    dataKey: "mission",
    label: ({ mission, location }) =>
        timestampLabel("Mission", mission?.timestamp, location),
    to: (context) =>
        urls.locationMission(
            routeId(context, "organizationId"),
            locationId(context),
            missionId(context),
        ),
});

export const flightsItem = defineItem({
    label: () => "Flights",
    to: (context) =>
        urls.flights(routeId(context, "organizationId"), locationId(context)),
});

export const flightItem = defineItem({
    dataKey: "flight",
    label: ({ flight, location }) =>
        timestampLabel("Flight", flight?.timestamp, location),
    to: (context) =>
        urls.flight(
            routeId(context, "organizationId"),
            locationId(context),
            flightId(context),
        ),
});

export const imagesItem = defineItem({
    label: () => "Images",
    to: (context) =>
        urls.measurements(
            routeId(context, "organizationId"),
            locationId(context),
        ),
});

export const imageItem = defineItem({
    label: () => "Image",
});

export const resultsItem = defineItem({
    label: () => "Results",
    to: (context) =>
        urls.locationResults(
            routeId(context, "organizationId"),
            locationId(context),
        ),
});

export const fileItem = defineItem({
    label: () => "File",
});

export const routinesItem = defineItem({
    label: () => "Routines",
    to: (context) =>
        urls.routines(routeId(context, "organizationId"), locationId(context)),
});

export const routineItem = defineItem({
    dataKey: "routine",
    label: ({ routine }) => routine?.name ?? "Routine",
    to: (context) =>
        urls.routine(
            routeId(context, "organizationId"),
            locationId(context),
            routeId(context, "routineId"),
        ),
});

export const globalAppsItem = defineItem({
    label: () => "Apps",
    to: () => urls.allApps(),
});

export const locationAppsItem = defineItem({
    label: () => "Apps",
    to: (context) =>
        urls.locationApps(
            routeId(context, "organizationId"),
            locationId(context),
        ),
});

export const appItem = defineItem({
    dataKey: "app",
    label: ({ app }) => app?.name ?? "App",
    to: (context) => {
        const organizationId = routeId(context, "organizationId");
        const currentLocationId = locationId(context);
        const appId = routeId(context, "appId");

        if (currentLocationId) {
            return `${urls.locationApps(organizationId, currentLocationId)}?appId=${appId}`;
        }

        return `${urls.allApps()}?appId=${appId}`;
    },
});

export const taskAppSettingsItem = defineItem({
    dataKey: "app",
    label: ({ app }) => `${app?.name ?? "App"} Settings`,
});

export const payloadsItem = defineItem({
    label: () => "Payloads",
    to: () => urls.payloads(),
});

export const payloadItem = defineItem({
    dataKey: "payload",
    label: ({ payload }) => payload?.name ?? "Payload",
    to: (context) => urls.payloadSettings(routeId(context, "payloadId")),
});

export const settingsItem = defineItem({
    label: () => "Settings",
});

export const editItem = defineItem({
    label: () => "Edit",
});

export const createLocationItem = defineItem({
    label: () => "Create Location",
});

export const createOrganizationItem = defineItem({
    label: () => "Create Organization",
});

export const createDroneItem = defineItem({
    label: () => "Create new drone",
});

export const nerdItem = defineItem({
    label: () => "Nerd",
});

export const livestreamItem = defineItem({
    label: () => "Livestream",
});

export const createItem = defineItem({
    label: () => "Create",
});

export const startItem = defineItem({
    label: () => "Start",
});
